/**
 * Hotels Directory Page
 * Comprehensive list of Hotels & Hotel Booking Agencies worldwide
 * Users can book accommodation 24/7 using their ParachPay Card
 */
import { useState, useMemo } from 'react';
import { Layout } from '../components/Layout';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Tabs, TabsList, TabsTrigger } from '../components/ui/tabs';
import { 
  Hotel, 
  Building2, 
  Globe, 
  Search, 
  ExternalLink, 
  MapPin,
  CreditCard,
  Clock,
  Plane,
  Star
} from 'lucide-react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';

// Comprehensive Hotels Directory Data
const hotelsDirectory = {
  "North America": {
    "United States": {
      hotels: [
        { name: "Marriott International", city: "Bethesda, MD", address: "7750 Wisconsin Avenue, Bethesda, MD 20814", url: "https://www.marriott.com", type: "hotel", stars: 5 },
        { name: "Hilton Hotels & Resorts", city: "McLean, VA", address: "7930 Jones Branch Drive, McLean, VA 22102", url: "https://www.hilton.com", type: "hotel", stars: 5 },
        { name: "Hyatt Hotels Corporation", city: "Chicago, IL", address: "150 N Riverside Plaza, Chicago, IL 60606", url: "https://www.hyatt.com", type: "hotel", stars: 5 },
        { name: "InterContinental Hotels Group", city: "Atlanta, GA", address: "3 Ravinia Drive, Atlanta, GA 30346", url: "https://www.ihg.com", type: "hotel", stars: 5 },
        { name: "Wyndham Hotels & Resorts", city: "Parsippany, NJ", address: "22 Sylvan Way, Parsippany, NJ 07054", url: "https://www.wyndhamhotels.com", type: "hotel", stars: 4 },
        { name: "Choice Hotels International", city: "Rockville, MD", address: "1 Choice Hotels Circle, Rockville, MD 20850", url: "https://www.choicehotels.com", type: "hotel", stars: 3 },
        { name: "Best Western Hotels", city: "Phoenix, AZ", address: "6201 N 24th Pkwy, Phoenix, AZ 85016", url: "https://www.bestwestern.com", type: "hotel", stars: 3 },
        { name: "Four Seasons Hotels", city: "Toronto/Global", address: "1165 Leslie Street, Toronto, ON M3C 2K8", url: "https://www.fourseasons.com", type: "hotel", stars: 5 },
        { name: "Ritz-Carlton", city: "Chevy Chase, MD", address: "4445 Willard Ave, Chevy Chase, MD 20815", url: "https://www.ritzcarlton.com", type: "hotel", stars: 5 },
        { name: "W Hotels", city: "New York, NY", address: "1567 Broadway, New York, NY 10036", url: "https://www.marriott.com/w-hotels", type: "hotel", stars: 5 }
      ],
      agencies: [
        { name: "Booking.com", city: "New York, NY", address: "75 Varick St, New York, NY 10013", url: "https://www.booking.com", type: "agency" },
        { name: "Expedia Hotels", city: "Seattle, WA", address: "1111 Expedia Group Way W, Seattle, WA 98119", url: "https://www.expedia.com/Hotels", type: "agency" },
        { name: "Hotels.com", city: "Dallas, TX", address: "5400 LBJ Freeway, Dallas, TX 75240", url: "https://www.hotels.com", type: "agency" },
        { name: "Trivago", city: "Düsseldorf (US Office)", address: "200 Park Avenue, New York, NY 10166", url: "https://www.trivago.com", type: "agency" },
        { name: "Priceline Hotels", city: "Norwalk, CT", address: "800 Connecticut Ave, Norwalk, CT 06854", url: "https://www.priceline.com/hotels", type: "agency" },
        { name: "Hotwire", city: "San Francisco, CA", address: "655 Montgomery Street, San Francisco, CA 94111", url: "https://www.hotwire.com", type: "agency" },
        { name: "Orbitz Hotels", city: "Chicago, IL", address: "500 W Madison St, Chicago, IL 60661", url: "https://www.orbitz.com/Hotels", type: "agency" },
        { name: "Travelocity Hotels", city: "Dallas, TX", address: "5400 LBJ Freeway, Dallas, TX 75240", url: "https://www.travelocity.com/Hotels", type: "agency" }
      ]
    },
    "Canada": {
      hotels: [
        { name: "Fairmont Hotels & Resorts", city: "Toronto, ON", address: "100 Wellington Street West, Toronto, ON M5K 1B7", url: "https://www.fairmont.com", type: "hotel", stars: 5 },
        { name: "Delta Hotels by Marriott", city: "Toronto, ON", address: "75 Lower Simcoe Street, Toronto, ON M5J 3A6", url: "https://www.marriott.com/delta-hotels", type: "hotel", stars: 4 },
        { name: "Sandman Hotels", city: "Vancouver, BC", address: "180 West Georgia Street, Vancouver, BC V6B 4P4", url: "https://www.sandmanhotels.com", type: "hotel", stars: 3 }
      ],
      agencies: [
        { name: "Expedia Canada", city: "Toronto, ON", address: "123 Front St W, Toronto, ON M5J 2M2", url: "https://www.expedia.ca/Hotels", type: "agency" },
        { name: "Trivago Canada", city: "Toronto, ON", address: "100 King Street West, Toronto, ON", url: "https://www.trivago.ca", type: "agency" }
      ]
    },
    "Mexico": {
      hotels: [
        { name: "Grupo Posadas", city: "Mexico City", address: "Paseo de la Reforma 155, Lomas de Chapultepec, CDMX", url: "https://www.posadas.com", type: "hotel", stars: 4 },
        { name: "Palace Resorts", city: "Cancun", address: "Boulevard Kukulcan Km 11.5, Cancún, Q.R.", url: "https://www.palaceresorts.com", type: "hotel", stars: 5 },
        { name: "Riu Hotels Mexico", city: "Cancun", address: "Boulevard Kukulcan Km 5.5, Cancún, Q.R.", url: "https://www.riu.com", type: "hotel", stars: 4 }
      ],
      agencies: [
        { name: "Despegar Mexico Hotels", city: "Mexico City", address: "Insurgentes Sur 1602, Benito Juárez, 03940 CDMX", url: "https://www.despegar.com.mx/hoteles", type: "agency" },
        { name: "Best Day Hotels", city: "Cancun", address: "Blvd. Kukulcan KM 9, Zona Hotelera, Cancún, Q.R.", url: "https://www.bestday.com/Hoteles", type: "agency" }
      ]
    }
  },
  "Europe": {
    "United Kingdom": {
      hotels: [
        { name: "Premier Inn", city: "Dunstable", address: "Premier House, 2 Cygnet Court, Dunstable LU5 4SE", url: "https://www.premierinn.com", type: "hotel", stars: 3 },
        { name: "Travelodge UK", city: "Thame", address: "Sleepy Hollow, Aylesbury Road, Thame OX9 3AT", url: "https://www.travelodge.co.uk", type: "hotel", stars: 2 },
        { name: "The Savoy London", city: "London", address: "Strand, London WC2R 0EZ", url: "https://www.thesavoylondon.com", type: "hotel", stars: 5 },
        { name: "Claridge's", city: "London", address: "Brook Street, Mayfair, London W1K 4HR", url: "https://www.claridges.co.uk", type: "hotel", stars: 5 },
        { name: "The Dorchester", city: "London", address: "53 Park Lane, Mayfair, London W1K 1QA", url: "https://www.dorchestercollection.com", type: "hotel", stars: 5 }
      ],
      agencies: [
        { name: "Booking.com UK", city: "London", address: "70 Mark Lane, London EC3R 7NQ", url: "https://www.booking.com", type: "agency" },
        { name: "LateRooms", city: "Manchester", address: "The Fountain Building, Central Park, Manchester M40 5BP", url: "https://www.laterooms.com", type: "agency" },
        { name: "TripAdvisor UK", city: "London", address: "7 Soho Square, London W1D 3QB", url: "https://www.tripadvisor.co.uk", type: "agency" }
      ]
    },
    "Germany": {
      hotels: [
        { name: "Steigenberger Hotels", city: "Frankfurt", address: "Lyoner Straße 25, 60528 Frankfurt", url: "https://www.steigenberger.com", type: "hotel", stars: 5 },
        { name: "Motel One", city: "Munich", address: "Sendlinger Straße 28, 80331 München", url: "https://www.motel-one.com", type: "hotel", stars: 3 },
        { name: "A&O Hostels", city: "Berlin", address: "Boxhagener Str. 73, 10245 Berlin", url: "https://www.aohostels.com", type: "hotel", stars: 2 },
        { name: "Kempinski Hotels", city: "Munich", address: "Maximilianstrasse 17, 80539 München", url: "https://www.kempinski.com", type: "hotel", stars: 5 }
      ],
      agencies: [
        { name: "HRS Germany", city: "Cologne", address: "Blaubach 32, 50676 Köln", url: "https://www.hrs.de", type: "agency" },
        { name: "Check24 Hotels", city: "Munich", address: "Erika-Mann-Straße 62, 80636 München", url: "https://www.check24.de/hotels", type: "agency" },
        { name: "Trivago Germany", city: "Düsseldorf", address: "Bennigsen-Platz 1, 40474 Düsseldorf", url: "https://www.trivago.de", type: "agency" }
      ]
    },
    "France": {
      hotels: [
        { name: "Accor Hotels", city: "Paris", address: "82 Rue Henri Farman, 92130 Issy-les-Moulineaux", url: "https://www.accor.com", type: "hotel", stars: 4 },
        { name: "Hôtel Plaza Athénée", city: "Paris", address: "25 Avenue Montaigne, 75008 Paris", url: "https://www.dorchestercollection.com/paris/hotel-plaza-athenee", type: "hotel", stars: 5 },
        { name: "Le Bristol Paris", city: "Paris", address: "112 Rue du Faubourg Saint-Honoré, 75008 Paris", url: "https://www.oetkercollection.com/hotels/le-bristol-paris", type: "hotel", stars: 5 },
        { name: "ibis Hotels", city: "Paris", address: "82 Rue Henri Farman, Issy-les-Moulineaux", url: "https://www.ibis.com", type: "hotel", stars: 2 }
      ],
      agencies: [
        { name: "Booking.com France", city: "Paris", address: "3 Rue de Castiglione, 75001 Paris", url: "https://www.booking.com/fr", type: "agency" },
        { name: "Trivago France", city: "Paris", address: "75 Avenue des Champs-Élysées, 75008 Paris", url: "https://www.trivago.fr", type: "agency" }
      ]
    },
    "Netherlands": {
      hotels: [
        { name: "NH Hotel Group", city: "Amsterdam", address: "Stadhouderskade 7, 1054 ES Amsterdam", url: "https://www.nh-hotels.com", type: "hotel", stars: 4 },
        { name: "Conservatorium Hotel", city: "Amsterdam", address: "Van Baerlestraat 27, 1071 AN Amsterdam", url: "https://www.conservatoriumhotel.com", type: "hotel", stars: 5 },
        { name: "Hotel V", city: "Amsterdam", address: "Victorieplein 42, 1078 PH Amsterdam", url: "https://www.hotelv.nl", type: "hotel", stars: 4 }
      ],
      agencies: [
        { name: "Booking.com HQ", city: "Amsterdam", address: "Herengracht 597, 1017 CE Amsterdam", url: "https://www.booking.com", type: "agency" },
        { name: "Trivago Netherlands", city: "Amsterdam", address: "Herengracht 182, 1016 BR Amsterdam", url: "https://www.trivago.nl", type: "agency" }
      ]
    },
    "Spain": {
      hotels: [
        { name: "Meliá Hotels International", city: "Palma de Mallorca", address: "Calle Gremio Toneleros 24, 07009 Palma", url: "https://www.melia.com", type: "hotel", stars: 4 },
        { name: "NH Hotels Spain", city: "Madrid", address: "Calle Santa Engracia 120, 28003 Madrid", url: "https://www.nh-hotels.com", type: "hotel", stars: 4 },
        { name: "Barceló Hotels", city: "Palma de Mallorca", address: "Calle José Rover Motta 27, 07006 Palma", url: "https://www.barcelo.com", type: "hotel", stars: 4 },
        { name: "Paradores de España", city: "Madrid", address: "Calle José Abascal 2-4, 28003 Madrid", url: "https://www.parador.es", type: "hotel", stars: 4 }
      ],
      agencies: [
        { name: "Booking.com Spain", city: "Barcelona", address: "Passeig de Gràcia 11, 08007 Barcelona", url: "https://www.booking.com/es", type: "agency" },
        { name: "Logitravel Hotels", city: "Palma de Mallorca", address: "Calle Gremi de Sabaters 21, 07009 Palma", url: "https://www.logitravel.com/hoteles", type: "agency" }
      ]
    },
    "Italy": {
      hotels: [
        { name: "Starhotels", city: "Florence", address: "Viale Belfiore 27, 50144 Firenze", url: "https://www.starhotels.com", type: "hotel", stars: 4 },
        { name: "Bettoja Hotels", city: "Rome", address: "Via Cavour 18, 00184 Roma", url: "https://www.bettojahotels.it", type: "hotel", stars: 4 },
        { name: "Hotel Hassler Roma", city: "Rome", address: "Piazza della Trinità dei Monti 6, 00187 Roma", url: "https://www.hotelhasslerroma.com", type: "hotel", stars: 5 },
        { name: "Belmond Hotel Cipriani", city: "Venice", address: "Giudecca 10, 30133 Venezia", url: "https://www.belmond.com/hotels/europe/italy/venice/belmond-hotel-cipriani", type: "hotel", stars: 5 }
      ],
      agencies: [
        { name: "Booking.com Italy", city: "Milan", address: "Via Brera 5, 20121 Milano", url: "https://www.booking.com/it", type: "agency" },
        { name: "Trivago Italy", city: "Milan", address: "Piazza Duomo 21, 20121 Milano", url: "https://www.trivago.it", type: "agency" }
      ]
    },
    "Switzerland": {
      hotels: [
        { name: "Badrutt's Palace Hotel", city: "St. Moritz", address: "Via Serlas 27, 7500 St. Moritz", url: "https://www.badruttspalace.com", type: "hotel", stars: 5 },
        { name: "The Dolder Grand", city: "Zurich", address: "Kurhausstrasse 65, 8032 Zürich", url: "https://www.thedoldergrand.com", type: "hotel", stars: 5 },
        { name: "Beau-Rivage Palace", city: "Lausanne", address: "Place du Port 17-19, 1006 Lausanne", url: "https://www.brp.ch", type: "hotel", stars: 5 }
      ],
      agencies: [
        { name: "Switzerland Tourism", city: "Zurich", address: "Morgartenstrasse 5a, 8004 Zürich", url: "https://www.myswitzerland.com", type: "agency" },
        { name: "Booking.com Switzerland", city: "Zurich", address: "Bahnhofstrasse 10, 8001 Zürich", url: "https://www.booking.com/ch", type: "agency" }
      ]
    },
    "Portugal": {
      hotels: [
        { name: "Pestana Hotels", city: "Lisbon", address: "Rua Jau 54, 1300-314 Lisboa", url: "https://www.pestana.com", type: "hotel", stars: 4 },
        { name: "Vila Vita Parc", city: "Algarve", address: "Rua Anneliese Pohl, 8400-450 Porches", url: "https://www.vilavitaparc.com", type: "hotel", stars: 5 }
      ],
      agencies: [
        { name: "Booking.com Portugal", city: "Lisbon", address: "Avenida da Liberdade 110, 1250-146 Lisboa", url: "https://www.booking.com/pt", type: "agency" }
      ]
    }
  },
  "Asia": {
    "United Arab Emirates": {
      hotels: [
        { name: "Burj Al Arab", city: "Dubai", address: "Jumeirah Beach Road, Dubai", url: "https://www.jumeirah.com/burj-al-arab", type: "hotel", stars: 5 },
        { name: "Atlantis The Palm", city: "Dubai", address: "Crescent Road, The Palm, Dubai", url: "https://www.atlantis.com/dubai", type: "hotel", stars: 5 },
        { name: "Emirates Palace", city: "Abu Dhabi", address: "West Corniche Road, Abu Dhabi", url: "https://www.mandarinoriental.com/abu-dhabi/emirates-palace", type: "hotel", stars: 5 },
        { name: "Jumeirah Hotels", city: "Dubai", address: "Jumeirah Beach Road, Dubai", url: "https://www.jumeirah.com", type: "hotel", stars: 5 },
        { name: "Rotana Hotels", city: "Abu Dhabi", address: "Khalifa Street, Abu Dhabi", url: "https://www.rotana.com", type: "hotel", stars: 4 }
      ],
      agencies: [
        { name: "Booking.com UAE", city: "Dubai", address: "Emirates Towers, Sheikh Zayed Road, Dubai", url: "https://www.booking.com/ae", type: "agency" },
        { name: "Cleartrip Hotels UAE", city: "Dubai", address: "Dubai Internet City, Dubai", url: "https://www.cleartrip.ae/hotels", type: "agency" }
      ]
    },
    "Singapore": {
      hotels: [
        { name: "Marina Bay Sands", city: "Singapore", address: "10 Bayfront Avenue, Singapore 018956", url: "https://www.marinabaysands.com", type: "hotel", stars: 5 },
        { name: "Raffles Hotel", city: "Singapore", address: "1 Beach Road, Singapore 189673", url: "https://www.raffles.com/singapore", type: "hotel", stars: 5 },
        { name: "The Fullerton Hotel", city: "Singapore", address: "1 Fullerton Square, Singapore 049178", url: "https://www.fullertonhotels.com", type: "hotel", stars: 5 },
        { name: "Capella Singapore", city: "Singapore", address: "1 The Knolls, Sentosa Island, Singapore 098297", url: "https://www.capellahotels.com/singapore", type: "hotel", stars: 5 }
      ],
      agencies: [
        { name: "Agoda Singapore", city: "Singapore", address: "30 Raffles Place, Singapore 048622", url: "https://www.agoda.com", type: "agency" },
        { name: "Traveloka Singapore", city: "Singapore", address: "1 Raffles Place, Singapore 048616", url: "https://www.traveloka.com/en-sg", type: "agency" }
      ]
    },
    "Japan": {
      hotels: [
        { name: "Aman Tokyo", city: "Tokyo", address: "The Otemachi Tower, 1-5-6 Otemachi, Chiyoda-ku", url: "https://www.aman.com/resorts/aman-tokyo", type: "hotel", stars: 5 },
        { name: "Park Hyatt Tokyo", city: "Tokyo", address: "3-7-1-2 Nishi-Shinjuku, Shinjuku-ku, Tokyo", url: "https://www.hyatt.com/park-hyatt-tokyo", type: "hotel", stars: 5 },
        { name: "The Peninsula Tokyo", city: "Tokyo", address: "1-8-1 Yurakucho, Chiyoda-ku, Tokyo", url: "https://www.peninsula.com/tokyo", type: "hotel", stars: 5 },
        { name: "APA Hotels", city: "Tokyo", address: "1-22-1 Kabukicho, Shinjuku-ku, Tokyo", url: "https://www.apahotel.com", type: "hotel", stars: 3 },
        { name: "Toyoko Inn", city: "Tokyo", address: "1-6-3 Nishi-Gotanda, Shinagawa-ku, Tokyo", url: "https://www.toyoko-inn.com", type: "hotel", stars: 2 }
      ],
      agencies: [
        { name: "Rakuten Travel", city: "Tokyo", address: "Rakuten Crimson House, 1-14-1 Tamagawa, Setagaya-ku", url: "https://travel.rakuten.com", type: "agency" },
        { name: "Jalan.net", city: "Tokyo", address: "2-10-2 Fujimi, Chiyoda-ku, Tokyo", url: "https://www.jalan.net", type: "agency" },
        { name: "Japanican", city: "Tokyo", address: "Shimbashi Trust Tower, 4-1-1 Shimbashi, Minato-ku", url: "https://www.japanican.com", type: "agency" }
      ]
    },
    "China": {
      hotels: [
        { name: "Jin Jiang International Hotels", city: "Shanghai", address: "59 Maoming South Road, Shanghai 200020", url: "https://www.jinjianghotels.com", type: "hotel", stars: 4 },
        { name: "Shangri-La Hotels", city: "Hong Kong", address: "Pacific Place, Supreme Court Road, Hong Kong", url: "https://www.shangri-la.com", type: "hotel", stars: 5 },
        { name: "Mandarin Oriental Hong Kong", city: "Hong Kong", address: "5 Connaught Road, Central, Hong Kong", url: "https://www.mandarinoriental.com/hong-kong", type: "hotel", stars: 5 },
        { name: "The Peninsula Hong Kong", city: "Hong Kong", address: "Salisbury Road, Kowloon, Hong Kong", url: "https://www.peninsula.com/hong-kong", type: "hotel", stars: 5 }
      ],
      agencies: [
        { name: "Ctrip/Trip.com Hotels", city: "Shanghai", address: "99 Fu Quan Road, Shanghai 200335", url: "https://www.trip.com/hotels", type: "agency" },
        { name: "eLong Hotels", city: "Beijing", address: "Zhongguancun, Beijing", url: "https://www.elong.com", type: "agency" },
        { name: "Fliggy Hotels", city: "Hangzhou", address: "Alibaba Campus, Hangzhou", url: "https://www.fliggy.com", type: "agency" }
      ]
    },
    "India": {
      hotels: [
        { name: "Taj Hotels", city: "Mumbai", address: "Apollo Bunder, Colaba, Mumbai 400001", url: "https://www.tajhotels.com", type: "hotel", stars: 5 },
        { name: "Oberoi Hotels", city: "New Delhi", address: "Dr. Zakir Hussain Marg, New Delhi 110003", url: "https://www.oberoihotels.com", type: "hotel", stars: 5 },
        { name: "ITC Hotels", city: "Kolkata", address: "1 Ho Chi Minh Sarani, Kolkata 700071", url: "https://www.itchotels.com", type: "hotel", stars: 5 },
        { name: "Leela Palaces", city: "Mumbai", address: "Sahar, Andheri East, Mumbai 400059", url: "https://www.theleela.com", type: "hotel", stars: 5 },
        { name: "OYO Rooms", city: "Gurgaon", address: "Building No. 5, DLF Cyber City, Gurgaon", url: "https://www.oyorooms.com", type: "hotel", stars: 2 }
      ],
      agencies: [
        { name: "MakeMyTrip Hotels", city: "Gurgaon", address: "19th Floor, Building No. 5, Tower A, DLF Cyber City", url: "https://www.makemytrip.com/hotels", type: "agency" },
        { name: "Goibibo Hotels", city: "Gurgaon", address: "Building No. 5, Tower A, DLF Cyber City, Gurgaon", url: "https://www.goibibo.com/hotels", type: "agency" },
        { name: "Yatra Hotels", city: "Gurgaon", address: "Unitech Infospace, Old Delhi-Gurgaon Road, Gurgaon", url: "https://www.yatra.com/hotels", type: "agency" },
        { name: "Cleartrip Hotels", city: "Mumbai", address: "Embassy 247, LBS Marg, Vikhroli, Mumbai", url: "https://www.cleartrip.com/hotels", type: "agency" }
      ]
    },
    "Thailand": {
      hotels: [
        { name: "Centara Hotels & Resorts", city: "Bangkok", address: "999/99 Rama 1 Road, Pathumwan, Bangkok", url: "https://www.centarahotelsresorts.com", type: "hotel", stars: 4 },
        { name: "Dusit Hotels", city: "Bangkok", address: "946 Rama IV Road, Silom, Bangkok", url: "https://www.dusit.com", type: "hotel", stars: 4 },
        { name: "Anantara Hotels", city: "Bangkok", address: "155 Rajadamri Road, Lumpini, Bangkok", url: "https://www.anantara.com", type: "hotel", stars: 5 },
        { name: "Banyan Tree Hotels", city: "Bangkok", address: "21/100 South Sathon Road, Bangkok", url: "https://www.banyantree.com", type: "hotel", stars: 5 }
      ],
      agencies: [
        { name: "Agoda Thailand", city: "Bangkok", address: "Amarin Tower, 496-502 Ploenchit Road, Bangkok", url: "https://www.agoda.com/th", type: "agency" },
        { name: "Traveloka Thailand", city: "Bangkok", address: "Silom Complex, Bangkok", url: "https://www.traveloka.com/th", type: "agency" }
      ]
    },
    "South Korea": {
      hotels: [
        { name: "Lotte Hotels", city: "Seoul", address: "30 Eulji-ro, Jung-gu, Seoul", url: "https://www.lottehotel.com", type: "hotel", stars: 5 },
        { name: "Shilla Hotels", city: "Seoul", address: "249 Dongho-ro, Jung-gu, Seoul", url: "https://www.shillahotels.com", type: "hotel", stars: 5 },
        { name: "Grand Hyatt Seoul", city: "Seoul", address: "322 Sowol-ro, Yongsan-gu, Seoul", url: "https://www.hyatt.com/grand-hyatt-seoul", type: "hotel", stars: 5 }
      ],
      agencies: [
        { name: "Yanolja", city: "Seoul", address: "Gangnam Finance Center, Yeoksam-dong, Seoul", url: "https://www.yanolja.com", type: "agency" },
        { name: "Booking.com Korea", city: "Seoul", address: "Gangnam-gu, Seoul", url: "https://www.booking.com/kr", type: "agency" }
      ]
    },
    "Malaysia": {
      hotels: [
        { name: "Genting Hotels", city: "Kuala Lumpur", address: "Genting Highlands Resort, Pahang", url: "https://www.rwgenting.com", type: "hotel", stars: 4 },
        { name: "Shangri-La Kuala Lumpur", city: "Kuala Lumpur", address: "11 Jalan Sultan Ismail, 50250 KL", url: "https://www.shangri-la.com/kualalumpur", type: "hotel", stars: 5 },
        { name: "Mandarin Oriental KL", city: "Kuala Lumpur", address: "Kuala Lumpur City Centre, 50088 KL", url: "https://www.mandarinoriental.com/kuala-lumpur", type: "hotel", stars: 5 }
      ],
      agencies: [
        { name: "Agoda Malaysia", city: "Kuala Lumpur", address: "Menara CIMB, Jalan Stesen Sentral 2", url: "https://www.agoda.com/my", type: "agency" },
        { name: "Traveloka Malaysia", city: "Kuala Lumpur", address: "Menara CIMB, Jalan Stesen Sentral 2", url: "https://www.traveloka.com/en-my/hotel", type: "agency" }
      ]
    }
  },
  "Middle East": {
    "Qatar": {
      hotels: [
        { name: "St. Regis Doha", city: "Doha", address: "West Bay, Doha", url: "https://www.marriott.com/st-regis-doha", type: "hotel", stars: 5 },
        { name: "Mondrian Doha", city: "Doha", address: "West Bay Lagoon, Doha", url: "https://www.sbe.com/hotels/mondrian/doha", type: "hotel", stars: 5 },
        { name: "Four Seasons Doha", city: "Doha", address: "The Corniche, Doha", url: "https://www.fourseasons.com/doha", type: "hotel", stars: 5 }
      ],
      agencies: [
        { name: "Booking.com Qatar", city: "Doha", address: "West Bay, Doha", url: "https://www.booking.com/qa", type: "agency" }
      ]
    },
    "Saudi Arabia": {
      hotels: [
        { name: "Ritz-Carlton Riyadh", city: "Riyadh", address: "AlHada Area, Mekkah Road, Riyadh", url: "https://www.ritzcarlton.com/riyadh", type: "hotel", stars: 5 },
        { name: "Four Seasons Riyadh", city: "Riyadh", address: "Kingdom Centre, Riyadh", url: "https://www.fourseasons.com/riyadh", type: "hotel", stars: 5 }
      ],
      agencies: [
        { name: "Almosafer Hotels", city: "Riyadh", address: "King Fahad Road, Riyadh", url: "https://www.almosafer.com/hotels", type: "agency" }
      ]
    },
    "Israel": {
      hotels: [
        { name: "David Citadel Hotel", city: "Jerusalem", address: "7 King David Street, Jerusalem", url: "https://www.thedavidcitadel.com", type: "hotel", stars: 5 },
        { name: "The Norman Tel Aviv", city: "Tel Aviv", address: "23-25 Nachmani Street, Tel Aviv", url: "https://www.thenorman.com", type: "hotel", stars: 5 },
        { name: "Dan Hotels", city: "Tel Aviv", address: "99 Hayarkon Street, Tel Aviv", url: "https://www.danhotels.com", type: "hotel", stars: 5 }
      ],
      agencies: [
        { name: "Booking.com Israel", city: "Tel Aviv", address: "Rothschild Boulevard, Tel Aviv", url: "https://www.booking.com/il", type: "agency" }
      ]
    },
    "Turkey": {
      hotels: [
        { name: "Çırağan Palace Kempinski", city: "Istanbul", address: "Çırağan Caddesi 32, Beşiktaş, Istanbul", url: "https://www.kempinski.com/istanbul", type: "hotel", stars: 5 },
        { name: "Four Seasons Istanbul", city: "Istanbul", address: "Tevkifhane Sokak 1, Sultanahmet, Istanbul", url: "https://www.fourseasons.com/istanbul", type: "hotel", stars: 5 },
        { name: "Dedeman Hotels", city: "Istanbul", address: "Yıldız Posta Caddesi 50, Gayrettepe, Istanbul", url: "https://www.dedeman.com", type: "hotel", stars: 4 }
      ],
      agencies: [
        { name: "Otelz.com", city: "Istanbul", address: "Maslak, Istanbul", url: "https://www.otelz.com", type: "agency" },
        { name: "Booking.com Turkey", city: "Istanbul", address: "Levent, Istanbul", url: "https://www.booking.com/tr", type: "agency" }
      ]
    }
  },
  "Africa": {
    "South Africa": {
      hotels: [
        { name: "Sun International", city: "Johannesburg", address: "6 Sandown Valley Crescent, Sandton, Johannesburg", url: "https://www.suninternational.com", type: "hotel", stars: 5 },
        { name: "Tsogo Sun Hotels", city: "Johannesburg", address: "Palazzo Towers East, Montecasino, Fourways", url: "https://www.tsogosun.com", type: "hotel", stars: 4 },
        { name: "Protea Hotels by Marriott", city: "Cape Town", address: "Convention Tower, Cape Town", url: "https://www.marriott.com/protea-hotels", type: "hotel", stars: 4 },
        { name: "The Table Bay Hotel", city: "Cape Town", address: "Quay 6, Victoria & Alfred Waterfront, Cape Town", url: "https://www.suninternational.com/table-bay", type: "hotel", stars: 5 }
      ],
      agencies: [
        { name: "Travelstart Hotels", city: "Cape Town", address: "The Colosseum, Century City, Cape Town", url: "https://www.travelstart.co.za/hotels", type: "agency" },
        { name: "Booking.com South Africa", city: "Johannesburg", address: "Sandton, Johannesburg", url: "https://www.booking.com/za", type: "agency" }
      ]
    },
    "Egypt": {
      hotels: [
        { name: "Marriott Mena House Cairo", city: "Cairo", address: "6 Pyramids Road, Giza, Cairo", url: "https://www.marriott.com/mena-house", type: "hotel", stars: 5 },
        { name: "Four Seasons Cairo", city: "Cairo", address: "35 Charles de Gaulle Street, Giza", url: "https://www.fourseasons.com/caironilepalace", type: "hotel", stars: 5 },
        { name: "Sofitel Winter Palace Luxor", city: "Luxor", address: "Corniche El Nile Street, Luxor", url: "https://www.sofitel-winter-palace-luxor.com", type: "hotel", stars: 5 }
      ],
      agencies: [
        { name: "Booking.com Egypt", city: "Cairo", address: "Zamalek, Cairo", url: "https://www.booking.com/eg", type: "agency" }
      ]
    },
    "Kenya": {
      hotels: [
        { name: "Sarova Hotels", city: "Nairobi", address: "Sarova Stanley, Kimathi Street, Nairobi", url: "https://www.sarovahotels.com", type: "hotel", stars: 4 },
        { name: "Hemingways Nairobi", city: "Nairobi", address: "Mbagathi Ridge, Nairobi", url: "https://www.hemingways-collection.com", type: "hotel", stars: 5 },
        { name: "Fairmont Mount Kenya Safari Club", city: "Nanyuki", address: "Nanyuki, Mount Kenya", url: "https://www.fairmont.com/mount-kenya-safari", type: "hotel", stars: 5 }
      ],
      agencies: [
        { name: "Booking.com Kenya", city: "Nairobi", address: "Westlands, Nairobi", url: "https://www.booking.com/ke", type: "agency" },
        { name: "Jumia Travel Kenya", city: "Nairobi", address: "Westlands, Nairobi", url: "https://travel.jumia.com/ke", type: "agency" }
      ]
    },
    "Morocco": {
      hotels: [
        { name: "La Mamounia", city: "Marrakech", address: "Avenue Bab Jdid, Marrakech 40040", url: "https://www.mamounia.com", type: "hotel", stars: 5 },
        { name: "Royal Mansour Marrakech", city: "Marrakech", address: "Rue Abou Abbas El Sebti, Marrakech", url: "https://www.royalmansour.com", type: "hotel", stars: 5 },
        { name: "Kasbah Tamadot", city: "Atlas Mountains", address: "BP 67, Asni, Atlas Mountains", url: "https://www.virginlimitededition.com/kasbah-tamadot", type: "hotel", stars: 5 }
      ],
      agencies: [
        { name: "Booking.com Morocco", city: "Casablanca", address: "Boulevard Mohammed V, Casablanca", url: "https://www.booking.com/ma", type: "agency" }
      ]
    },
    "Nigeria": {
      hotels: [
        { name: "Eko Hotels & Suites", city: "Lagos", address: "Plot 1415 Adetokunbo Ademola Street, Victoria Island, Lagos", url: "https://www.ekohotels.com", type: "hotel", stars: 5 },
        { name: "Transcorp Hilton Abuja", city: "Abuja", address: "1 Aguiyi Ironsi Street, Maitama, Abuja", url: "https://www.hilton.com/transcorp-hilton-abuja", type: "hotel", stars: 5 },
        { name: "Four Points by Sheraton Lagos", city: "Lagos", address: "Plot 9/10 Block 2, Oniru Chieftaincy Estate, Victoria Island", url: "https://www.marriott.com/four-points-lagos", type: "hotel", stars: 4 }
      ],
      agencies: [
        { name: "Jumia Travel Nigeria", city: "Lagos", address: "King's Tower, Ozumba Mbadiwe Avenue, Lagos", url: "https://travel.jumia.com/ng", type: "agency" },
        { name: "Hotels.ng", city: "Lagos", address: "Victoria Island, Lagos", url: "https://www.hotels.ng", type: "agency" }
      ]
    }
  },
  "Oceania": {
    "Australia": {
      hotels: [
        { name: "Crown Hotels", city: "Melbourne", address: "8 Whiteman Street, Southbank, Melbourne VIC 3006", url: "https://www.crownhotels.com.au", type: "hotel", stars: 5 },
        { name: "InterContinental Sydney", city: "Sydney", address: "117 Macquarie Street, Sydney NSW 2000", url: "https://www.ihg.com/intercontinental/sydney", type: "hotel", stars: 5 },
        { name: "Park Hyatt Sydney", city: "Sydney", address: "7 Hickson Road, The Rocks, Sydney NSW 2000", url: "https://www.hyatt.com/park-hyatt-sydney", type: "hotel", stars: 5 },
        { name: "Qualia Resort", city: "Hamilton Island", address: "20 Whitsunday Boulevard, Hamilton Island QLD 4803", url: "https://www.qualia.com.au", type: "hotel", stars: 5 }
      ],
      agencies: [
        { name: "Wotif", city: "Brisbane", address: "Level 8, 189 Grey Street, South Brisbane QLD 4101", url: "https://www.wotif.com", type: "agency" },
        { name: "Webjet Hotels", city: "Melbourne", address: "Level 2, 509 St Kilda Road, Melbourne", url: "https://www.webjet.com.au/hotels", type: "agency" },
        { name: "lastminute.com.au", city: "Sydney", address: "100 Pacific Highway, North Sydney NSW 2060", url: "https://www.lastminute.com.au", type: "agency" }
      ]
    },
    "New Zealand": {
      hotels: [
        { name: "Huka Lodge", city: "Taupo", address: "271 Huka Falls Road, Taupo 3377", url: "https://www.hukalodge.co.nz", type: "hotel", stars: 5 },
        { name: "The Langham Auckland", city: "Auckland", address: "83 Symonds Street, Auckland 1010", url: "https://www.langhamhotels.com/auckland", type: "hotel", stars: 5 },
        { name: "Millbrook Resort", city: "Queenstown", address: "1124 Malaghans Road, Arrowtown 9371", url: "https://www.millbrook.co.nz", type: "hotel", stars: 5 }
      ],
      agencies: [
        { name: "Booking.com New Zealand", city: "Auckland", address: "Queen Street, Auckland CBD", url: "https://www.booking.com/nz", type: "agency" },
        { name: "Wotif New Zealand", city: "Auckland", address: "Auckland CBD", url: "https://www.wotif.co.nz", type: "agency" }
      ]
    },
    "Fiji": {
      hotels: [
        { name: "Likuliku Lagoon Resort", city: "Malolo Island", address: "Malolo Island, Mamanuca Islands", url: "https://www.likulikulagoon.com", type: "hotel", stars: 5 },
        { name: "Six Senses Fiji", city: "Malolo Island", address: "Malolo Island, Mamanuca Islands", url: "https://www.sixsenses.com/fiji", type: "hotel", stars: 5 }
      ],
      agencies: [
        { name: "Booking.com Fiji", city: "Suva", address: "Victoria Parade, Suva", url: "https://www.booking.com/fj", type: "agency" }
      ]
    }
  },
  "South America": {
    "Brazil": {
      hotels: [
        { name: "Belmond Copacabana Palace", city: "Rio de Janeiro", address: "Av. Atlântica 1702, Copacabana, Rio de Janeiro", url: "https://www.belmond.com/copacabana-palace", type: "hotel", stars: 5 },
        { name: "Hotel Fasano", city: "São Paulo", address: "Rua Vittorio Fasano 88, Jardim Paulista, São Paulo", url: "https://www.fasano.com.br", type: "hotel", stars: 5 },
        { name: "Tivoli Mofarrej São Paulo", city: "São Paulo", address: "Alameda Santos 1437, Cerqueira César, São Paulo", url: "https://www.tivolihotels.com/sao-paulo", type: "hotel", stars: 5 }
      ],
      agencies: [
        { name: "Decolar Hotels", city: "São Paulo", address: "Av. das Nações Unidas 14171, São Paulo", url: "https://www.decolar.com/hoteis", type: "agency" },
        { name: "Hotel Urbano", city: "Rio de Janeiro", address: "Praia de Botafogo 501, Rio de Janeiro", url: "https://www.hotelurbano.com", type: "agency" },
        { name: "Booking.com Brazil", city: "São Paulo", address: "Av. Paulista, São Paulo", url: "https://www.booking.com/br", type: "agency" }
      ]
    },
    "Argentina": {
      hotels: [
        { name: "Alvear Palace Hotel", city: "Buenos Aires", address: "Av. Alvear 1891, Recoleta, Buenos Aires", url: "https://www.alvearpalace.com", type: "hotel", stars: 5 },
        { name: "Faena Hotel Buenos Aires", city: "Buenos Aires", address: "Martha Salotti 445, Puerto Madero, Buenos Aires", url: "https://www.faena.com/buenos-aires", type: "hotel", stars: 5 },
        { name: "Park Hyatt Buenos Aires", city: "Buenos Aires", address: "Av. Alvear 1661, Recoleta, Buenos Aires", url: "https://www.hyatt.com/park-hyatt-buenos-aires", type: "hotel", stars: 5 }
      ],
      agencies: [
        { name: "Despegar Hotels Argentina", city: "Buenos Aires", address: "Av. Santa Fe 1701, Buenos Aires", url: "https://www.despegar.com.ar/hoteles", type: "agency" },
        { name: "Booking.com Argentina", city: "Buenos Aires", address: "Puerto Madero, Buenos Aires", url: "https://www.booking.com/ar", type: "agency" }
      ]
    },
    "Chile": {
      hotels: [
        { name: "The Singular Santiago", city: "Santiago", address: "Merced 294, Lastarria, Santiago", url: "https://www.thesingular.com/santiago", type: "hotel", stars: 5 },
        { name: "W Santiago", city: "Santiago", address: "Isidora Goyenechea 3000, Las Condes, Santiago", url: "https://www.marriott.com/w-santiago", type: "hotel", stars: 5 },
        { name: "explora Patagonia", city: "Torres del Paine", address: "Torres del Paine National Park, Patagonia", url: "https://www.explora.com/patagonia", type: "hotel", stars: 5 }
      ],
      agencies: [
        { name: "Despegar Chile Hotels", city: "Santiago", address: "Av. Providencia 1760, Santiago", url: "https://www.despegar.cl/hoteles", type: "agency" },
        { name: "Booking.com Chile", city: "Santiago", address: "Las Condes, Santiago", url: "https://www.booking.com/cl", type: "agency" }
      ]
    },
    "Colombia": {
      hotels: [
        { name: "Four Seasons Bogotá", city: "Bogotá", address: "Cra. 7 No. 69-22, Bogotá", url: "https://www.fourseasons.com/bogota", type: "hotel", stars: 5 },
        { name: "Sofitel Legend Santa Clara", city: "Cartagena", address: "Calle del Torno No. 39-29, Cartagena", url: "https://www.sofitel-legend-santaclara-cartagena.com", type: "hotel", stars: 5 },
        { name: "Hotel Caribe by Faranda", city: "Cartagena", address: "Carrera 1 No. 2-87, Bocagrande, Cartagena", url: "https://www.hotelcaribe.com", type: "hotel", stars: 4 }
      ],
      agencies: [
        { name: "Despegar Colombia Hotels", city: "Bogotá", address: "Calle 100 No. 8A-49, Bogotá", url: "https://www.despegar.com.co/hoteles", type: "agency" },
        { name: "Booking.com Colombia", city: "Bogotá", address: "Chapinero, Bogotá", url: "https://www.booking.com/co", type: "agency" }
      ]
    },
    "Peru": {
      hotels: [
        { name: "Belmond Hotel Monasterio", city: "Cusco", address: "Calle Palacio 136, Plazoleta Nazarenas, Cusco", url: "https://www.belmond.com/hotel-monasterio", type: "hotel", stars: 5 },
        { name: "Belmond Sanctuary Lodge", city: "Machu Picchu", address: "Machu Picchu, Cusco Region", url: "https://www.belmond.com/sanctuary-lodge", type: "hotel", stars: 5 },
        { name: "JW Marriott Lima", city: "Lima", address: "Malecón de la Reserva 615, Miraflores, Lima", url: "https://www.marriott.com/jw-marriott-lima", type: "hotel", stars: 5 }
      ],
      agencies: [
        { name: "Despegar Peru Hotels", city: "Lima", address: "Av. Camino Real 456, San Isidro, Lima", url: "https://www.despegar.com.pe/hoteles", type: "agency" },
        { name: "Booking.com Peru", city: "Lima", address: "Miraflores, Lima", url: "https://www.booking.com/pe", type: "agency" }
      ]
    }
  }
};

const HotelsDirectory = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedContinent, setSelectedContinent] = useState('all');
  const [filterType, setFilterType] = useState('all');

  const continents = Object.keys(hotelsDirectory);

  const filteredEntries = useMemo(() => {
    let entries = [];
    
    const continentsToSearch = selectedContinent === 'all' 
      ? continents 
      : [selectedContinent];
    
    continentsToSearch.forEach(continent => {
      const countries = hotelsDirectory[continent];
      Object.entries(countries).forEach(([country, data]) => {
        const allItems = [...(data.hotels || []), ...(data.agencies || [])];
        allItems.forEach(item => {
          entries.push({
            ...item,
            country,
            continent
          });
        });
      });
    });
    
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      entries = entries.filter(item => 
        item.name.toLowerCase().includes(query) ||
        item.city.toLowerCase().includes(query) ||
        item.country.toLowerCase().includes(query) ||
        item.continent.toLowerCase().includes(query)
      );
    }
    
    if (filterType !== 'all') {
      entries = entries.filter(item => item.type === filterType);
    }
    
    return entries;
  }, [searchQuery, selectedContinent, filterType, continents]);

  const stats = useMemo(() => {
    let totalHotels = 0;
    let totalAgencies = 0;
    let totalCountries = 0;
    
    Object.values(hotelsDirectory).forEach(countries => {
      totalCountries += Object.keys(countries).length;
      Object.values(countries).forEach(data => {
        totalHotels += (data.hotels || []).length;
        totalAgencies += (data.agencies || []).length;
      });
    });
    
    return { totalHotels, totalAgencies, totalCountries, totalContinents: continents.length };
  }, [continents]);

  const renderStars = (count) => {
    return Array(count).fill(0).map((_, i) => (
      <Star key={`star-${i}`} className="w-3 h-3 fill-amber-400 text-amber-400" />
    ));
  };

  return (
    <Layout>
      <motion.div 
        className="space-y-8"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        data-testid="hotels-directory-page"
      >
        {/* Header */}
        <div className="text-center max-w-3xl mx-auto">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-blue-500/10 text-blue-600 dark:text-blue-400 text-sm font-medium mb-4">
            <Hotel className="w-4 h-4" />
            Global Hotels Directory
          </div>
          <h1 className="text-3xl md:text-4xl font-heading font-bold tracking-tight mb-4">
            Hotels & Booking Agencies Directory
          </h1>
          <p className="text-lg text-muted-foreground">
            Discover hotels and booking agencies from every country. Book directly with the hotel or agency of your choice.
          </p>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card>
            <CardContent className="pt-6 text-center">
              <Hotel className="w-8 h-8 mx-auto text-blue-600 mb-2" />
              <p className="text-3xl font-bold">{stats.totalHotels}</p>
              <p className="text-sm text-muted-foreground">Hotels</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6 text-center">
              <Building2 className="w-8 h-8 mx-auto text-emerald-600 mb-2" />
              <p className="text-3xl font-bold">{stats.totalAgencies}</p>
              <p className="text-sm text-muted-foreground">Booking Agencies</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6 text-center">
              <Globe className="w-8 h-8 mx-auto text-purple-600 mb-2" />
              <p className="text-3xl font-bold">{stats.totalCountries}</p>
              <p className="text-sm text-muted-foreground">Countries</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6 text-center">
              <CreditCard className="w-8 h-8 mx-auto text-orange-600 mb-2" />
              <p className="text-3xl font-bold">24/7</p>
              <p className="text-sm text-muted-foreground">Booking Available</p>
            </CardContent>
          </Card>
        </div>

        {/* Combined Booking Banner */}
        <Card className="bg-gradient-to-r from-blue-600 to-blue-700 text-white border-0">
          <CardContent className="py-6">
            <div className="flex flex-col md:flex-row items-center justify-between gap-4">
              <div className="flex items-center gap-4">
                <div className="flex gap-2">
                  <Hotel className="w-10 h-10" />
                  <Plane className="w-10 h-10" />
                </div>
                <div>
                  <h3 className="text-xl font-bold">Book Hotels + Flights Together</h3>
                  <p className="text-blue-100">Find accommodation and flights in one place, then book directly with each provider</p>
                </div>
              </div>
              <Link 
                to="/travel-directory"
                className="flex items-center gap-2 bg-white/20 hover:bg-white/30 px-4 py-2 rounded-full transition-colors"
              >
                <Plane className="w-4 h-4" />
                View Airlines Directory
              </Link>
            </div>
          </CardContent>
        </Card>

        {/* Search and Filters */}
        <div className="flex flex-col md:flex-row gap-4">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              placeholder="Search hotels, agencies, countries, or cities..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10 rounded-xl"
            />
          </div>
          <div className="flex gap-2">
            <Button
              variant={filterType === 'all' ? 'default' : 'outline'}
              onClick={() => setFilterType('all')}
              className="rounded-xl"
            >
              All
            </Button>
            <Button
              variant={filterType === 'hotel' ? 'default' : 'outline'}
              onClick={() => setFilterType('hotel')}
              className="rounded-xl"
            >
              <Hotel className="w-4 h-4 mr-2" />
              Hotels
            </Button>
            <Button
              variant={filterType === 'agency' ? 'default' : 'outline'}
              onClick={() => setFilterType('agency')}
              className="rounded-xl"
            >
              <Building2 className="w-4 h-4 mr-2" />
              Agencies
            </Button>
          </div>
        </div>

        {/* Continent Tabs */}
        <Tabs value={selectedContinent} onValueChange={setSelectedContinent} className="w-full">
          <TabsList className="flex flex-wrap h-auto gap-2 bg-transparent p-0 mb-6">
            <TabsTrigger 
              value="all" 
              className="rounded-xl data-[state=active]:bg-blue-600 data-[state=active]:text-white"
            >
              All Regions
            </TabsTrigger>
            {continents.map(continent => (
              <TabsTrigger 
                key={continent} 
                value={continent}
                className="rounded-xl data-[state=active]:bg-blue-600 data-[state=active]:text-white"
              >
                {continent}
              </TabsTrigger>
            ))}
          </TabsList>

          {/* Results */}
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground">
              Showing {filteredEntries.length} results
            </p>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {filteredEntries.map((item, idx) => (
                <motion.div
                  key={`${item.name}-${idx}`}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: idx * 0.02 }}
                >
                  <Card className="h-full hover:shadow-lg transition-shadow">
                    <CardContent className="pt-6">
                      <div className="flex items-start justify-between gap-2 mb-3">
                        <div className="flex items-center gap-2">
                          {item.type === 'hotel' ? (
                            <div className="p-2 rounded-lg bg-blue-500/10">
                              <Hotel className="w-5 h-5 text-blue-600" />
                            </div>
                          ) : (
                            <div className="p-2 rounded-lg bg-emerald-500/10">
                              <Building2 className="w-5 h-5 text-emerald-600" />
                            </div>
                          )}
                          <Badge variant="outline" className="text-xs">
                            {item.type === 'hotel' ? 'Hotel' : 'Agency'}
                          </Badge>
                        </div>
                        <Badge className="bg-muted text-muted-foreground text-xs">
                          {item.country}
                        </Badge>
                      </div>
                      
                      <h3 className="font-semibold text-lg mb-1">{item.name}</h3>
                      
                      {item.stars && (
                        <div className="flex items-center gap-0.5 mb-2">
                          {renderStars(item.stars)}
                        </div>
                      )}
                      
                      <div className="space-y-2 text-sm text-muted-foreground mb-4">
                        <div className="flex items-start gap-2">
                          <MapPin className="w-4 h-4 flex-shrink-0 mt-0.5" />
                          <span>{item.city}</span>
                        </div>
                        <p className="text-xs pl-6">{item.address}</p>
                      </div>
                      
                      <Button 
                        variant="outline" 
                        className="w-full rounded-xl"
                        asChild
                      >
                        <a 
                          href={item.url} 
                          target="_blank" 
                          rel="noopener noreferrer"
                        >
                          <ExternalLink className="w-4 h-4 mr-2" />
                          Visit Website
                        </a>
                      </Button>
                    </CardContent>
                  </Card>
                </motion.div>
              ))}
            </div>
            
            {filteredEntries.length === 0 && (
              <Card className="text-center py-12">
                <CardContent>
                  <Globe className="w-12 h-12 mx-auto text-muted-foreground/30 mb-4" />
                  <p className="text-muted-foreground">No results found</p>
                  <p className="text-sm text-muted-foreground">Try adjusting your search or filters</p>
                </CardContent>
              </Card>
            )}
          </div>
        </Tabs>
      </motion.div>
    </Layout>
  );
};

export default HotelsDirectory;
